import { useRouting } from '@/hooks'
import { Alert, Api, ApiRunFunc } from '@/modules'
import { Paged, UploadResponse } from '@shared/models'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

interface UploadsContextOutput {
  uploads?: Paged<UploadResponse>
  isLoadingUploads: boolean
  loadUploads: ApiRunFunc

  selectedUploadIds: Set<string>
  selectAllVisible: boolean
  toggleUploadSelection: (uploadId: string, companyId: string) => void
  toggleSelectAllVisible: () => void
  clearSelection: () => void

  batchDeleteUploads: () => Promise<void>
  isDeletingBatch: boolean
}

const UploadsContext = createContext<UploadsContextOutput>({} as UploadsContextOutput)

export function UploadsContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { companyID, queryParams } = useRouting()
  const currentPage: number = Number(queryParams.pagina ?? '1')

  const { sendAlert } = Alert.Hook()

  const [selectedUploadIds, setSelectedUploadIds] = useState<Set<string>>(new Set())

  const [selectAllVisible, setSelectAllVisible] = useState(false)

  const [loadUploads, isLoadingUploads, uploads] = Api.LazyQuery<Paged<UploadResponse>>(
    `companies/${companyID}/spreadsheets`,
  )

  const { isRunning: isDeletingBatch, run: batchDeleteUploads } = Api.Command<{ uploadIds: string[] }>(
    Api.Methods.DELETE,
    `companies/:companyID/spreadsheets/batch`,
  )

  const toggleUploadSelection = (uploadId: string, companyId: string) => {
    const compositeId = `${uploadId}_${companyId}`

    setSelectedUploadIds((prev) => {
      const newSet = new Set(prev)

      if (newSet.has(compositeId)) {
        newSet.delete(compositeId)
      } else {
        newSet.add(compositeId)
      }

      return newSet
    })
  }

  const toggleSelectAllVisible = () => {
    if (selectAllVisible) {
      setSelectedUploadIds(new Set())
    } else {
      const visibleCompositeIds = uploads?.elements.map((upload) => `${upload.id}_${companyID}`) || []
      const uniqueIds = Array.from(new Set(visibleCompositeIds))
      setSelectedUploadIds(new Set(uniqueIds))
    }
    setSelectAllVisible(!selectAllVisible)
  }

  const clearSelection = () => {
    setSelectedUploadIds(new Set())
    setSelectAllVisible(false)
  }

  const handleBatchDelete = async (): Promise<void> => {
    if (selectedUploadIds.size === 0) return

    try {
      const uploadIdsForBackend = Array.from(selectedUploadIds).map((compositeId) => compositeId.split('_')[0])

      batchDeleteUploads({
        input: {
          uploadIds: uploadIdsForBackend,
        },
        binds: [['companyID', companyID!]],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Uploads excluídos',
            message: `${selectedUploadIds.size} uploads foram excluídos com sucesso.`,
          })

          clearSelection()
          loadUploads({
            size: 15,
            page: currentPage,
          })
        },
        onFail: (error) => {
          sendAlert({
            type: 'error',
            title: 'Erro ao excluir uploads',
            message: `Não foi possível excluir os uploads selecionados: ${error.message}`,
          })
        },
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao excluir uploads',
        message: 'Não foi possível excluir os uploads selecionados.',
      })
    }
  }

  useEffect(() => {
    if (!uploads?.elements.length) {
      setSelectAllVisible(false)
      return
    }

    const visibleCompositeIds = uploads.elements.map((upload) => `${upload.id}_${companyID}`)
    const uniqueVisibleIds = Array.from(new Set(visibleCompositeIds))
    const allSelected = uniqueVisibleIds.every((compositeId) => selectedUploadIds.has(compositeId))
    setSelectAllVisible(allSelected)
  }, [selectedUploadIds, uploads])

  useEffect(() => {
    clearSelection()
  }, [currentPage])

  return (
    <UploadsContext.Provider
      value={{
        uploads,
        isLoadingUploads,
        loadUploads,
        selectedUploadIds,
        toggleUploadSelection,
        selectAllVisible,
        toggleSelectAllVisible,
        clearSelection,
        batchDeleteUploads: handleBatchDelete,
        isDeletingBatch,
      }}>
      {children}
    </UploadsContext.Provider>
  )
}

export function useUploadsContext(): UploadsContextOutput {
  const context = useContext(UploadsContext)
  if (!context) throw new Error('useUploadsContext deve ser chamado dentro do contexto correto')
  return context
}
